import React from "react";
import { ClaimModel } from "../../models/ClaimModel";
import { Option } from "../../shared/fields/Options";
import { BusinessCategories, customRound, formatNumber, PerformanceMeasures } from "../../shared/Utility";
import { calculateEventPromoterTotal } from "./schedules/Earnings/EventPromoterEntryDetails";
import { calculateGoodsTotal } from "./schedules/Earnings/GoodsEntryDetails";
import { calculateRelatedEntityTotal } from "./schedules/Earnings/RelatedEntityEntryDetails";
import { calculateServicesIPRKnowHowTotal } from "./schedules/Earnings/ServicesIPRKnowHowEntryDetails";
import { calculateTourismServicesTotal } from "./schedules/Earnings/TourismServicesEntryDetails";
import { calculateAdvertisingTotal } from "./schedules/Expenses/AdvertisingEntryDetails";
import { calculateFreeSampleTotal } from "./schedules/Expenses/FreeSampleEntryDetails";
import { calculateIntellectualPropertyInsuranceTotal } from "./schedules/Expenses/IntellectualPropertyInsuranceEntryDetails";
import { calculateIntellectualPropertyRegistrationTotal } from "./schedules/Expenses/IntellectualPropertyRegistrationEntryDetails";
import { calculateAllConsultantsTotal } from "./schedules/Expenses/MarketingConsultantDetails";
import { calculateMarketingVisitTotal } from "./schedules/Expenses/MarketingVisitEntryDetails";
import { calculateOverseasBuyerTotal } from "./schedules/Expenses/OverseasBuyerEntryDetails";
import { calculatePromotionalEventTotal } from "./schedules/Expenses/PromotionalEventEntryDetails";
import { calculateAllRepresentativesTotal } from "./schedules/Expenses/RepresentationDetails";

export interface ExpenditureTotals {
    representationTotal: number
    marketingConsultantTotal: number
    marketingVisitTotal: number
    freeSampleTotal: number
    promotionalEventTotal: number
    advertisingTotal: number
    overseasBuyerTotal: number
    intellectualPropertyRegistrationTotal: number
    intellectualPropertyInsuranceTotal: number

    total: number
    totalCapped: number
}

export interface EarningTotals {
    goodsTotal: number
    servicesIPRKnowHowTotal: number
    tourismServicesTotal: number
    eventPromoterTotal: number
    relatedEntityTotal: number

    total: number
}

export const calculateExpenses = (model: ClaimModel): ExpenditureTotals => {
    //Expense totals
    let total = 0;
    let totalCapped = 0;

    let representationTotal = 0;
    let marketingConsultantTotal = 0;
    let marketingVisitTotal = 0;
    let freeSampleTotal = 0;
    let promotionalEventTotal = 0;
    let advertisingTotal = 0;
    let overseasBuyerTotal = 0;
    let intellectualPropertyRegistrationTotal = 0;
    let intellectualPropertyInsuranceTotal = 0;

    //Schedule 1A - Overseas Representative - Capped at $200,000
    if (model.claimOverseasRepExpenses) {
        representationTotal = calculateAllRepresentativesTotal(model.representations);
        totalCapped += Math.min(representationTotal, 200000);
        total += representationTotal;
    }

    //Schedule 1B - Marketing Consultant - Capped at $50,000
    if (model.claimMarketingConsultantExpenses) {
        marketingConsultantTotal = calculateAllConsultantsTotal(model.marketingConsultants);
        totalCapped += Math.min(marketingConsultantTotal, 50000);
        total += marketingConsultantTotal;
    }

    //Schedule 2 – Marketing Visits
    if (model.claimMarketingVisitExpenses) {
        marketingVisitTotal = calculateMarketingVisitTotal(model.marketingVisitEntries);
        totalCapped += marketingVisitTotal;
        total += marketingVisitTotal;
    }

    //Schedule 4 – Free Samples - Capped at $15,000
    if (model.claimFreeSampleExpenses) {
        freeSampleTotal = calculateFreeSampleTotal(model.freeSampleEntries);
        totalCapped += Math.min(freeSampleTotal, 15000);
        total += freeSampleTotal;
    }

    //Schedule 5 – Trade Fairs & Promotional Events
    if (model.claimTradeFairsPromotionalEventsExpenses) {
        promotionalEventTotal = calculatePromotionalEventTotal(model.promotionalEventEntries);
        totalCapped += promotionalEventTotal;
        total += promotionalEventTotal;
    }

    //Schedule 6 – Promotional Literature & Advertising
    if (model.claimPromotionalLiteratureAdvertisingExpenses) {
        advertisingTotal = calculateAdvertisingTotal(model.advertisingEntries);
        totalCapped += advertisingTotal;
        total += advertisingTotal;
    }

    //Schedule 7 – Overseas Buyers - Capped at $45,000
    if (model.claimOverseasBuyersExpenses) {
        overseasBuyerTotal = calculateOverseasBuyerTotal(model.overseasBuyerEntries);
        totalCapped += Math.min(overseasBuyerTotal, 45000);
        total += overseasBuyerTotal;
    }

    //Schedule 8 – Intellectual Property Registration - Capped at $50,000
    if (model.claimIpRegistrationExpenses) {
        intellectualPropertyRegistrationTotal = calculateIntellectualPropertyRegistrationTotal(model.intellectualPropertyRegistrationEntries);
        totalCapped += Math.min(intellectualPropertyRegistrationTotal, 50000);    
        total += intellectualPropertyRegistrationTotal;
    }

    //Schedule 9 – Intellectual Property Insurance
    if (model.claimIpInsuranceExpenses) {
        intellectualPropertyInsuranceTotal = calculateIntellectualPropertyInsuranceTotal(model.intellectualPropertyInsuranceEntries);
        totalCapped += intellectualPropertyInsuranceTotal;
        total += intellectualPropertyInsuranceTotal;
    }

    return {
        representationTotal,
        marketingConsultantTotal,
        marketingVisitTotal,
        freeSampleTotal,
        promotionalEventTotal,
        advertisingTotal,
        overseasBuyerTotal,
        intellectualPropertyRegistrationTotal,
        intellectualPropertyInsuranceTotal,
        totalCapped,
        total
    }
}

export const calculateEarnings = (model: ClaimModel): EarningTotals => {
    //Earning totals
    let total = 0;

    let goodsTotal = 0;
    let servicesIPRKnowHowTotal = 0;
    let tourismServicesTotal = 0;
    let eventPromoterTotal = 0;
    let relatedEntityTotal = 0;

    //Schedule 10A – Export Earnings – Goods
    if (model.claimGoodEarnings) {
        goodsTotal = calculateGoodsTotal(model.goodsEntries);
        total += goodsTotal;
    }

    //Schedule 10B – Export Earnings – Services/IPR/Know-How
    if (model.claimServiceEarnings) {
        servicesIPRKnowHowTotal = calculateServicesIPRKnowHowTotal(model.servicesIPRKnowHowEntries);
        total += servicesIPRKnowHowTotal;
    }

    //Schedule 10C – Export Earnings – Tourism Services
    if (model.claimTourismEarnings) {
        tourismServicesTotal = calculateTourismServicesTotal(model.tourismServicesEntries);
        total += tourismServicesTotal
    }

    //Schedule 10D – Export Earnings – Events Promoters
    if (model.claimEventEarnings) {
        eventPromoterTotal = calculateEventPromoterTotal(model.eventPromoterEntries);
        total += eventPromoterTotal;
    }

    //Schedule 10E – Export Earnings – Related Entities
    if (model.claimEntityEarnings) {
        relatedEntityTotal = calculateRelatedEntityTotal(model.relatedEntityEntries);
        total += relatedEntityTotal;
    }
        

    return {
        goodsTotal,
        servicesIPRKnowHowTotal,
        tourismServicesTotal,
        eventPromoterTotal,
        relatedEntityTotal,
        total 
    };
}

export interface GrantEstimationProps {
    model: ClaimModel
    showSummary?: boolean
}

export const GrantEstimation: React.FC<GrantEstimationProps> = (props: GrantEstimationProps) => {

    let expenditure = calculateExpenses(props.model);
    let earnigs = calculateEarnings(props.model);


    let totalLessThreshold = Math.max(expenditure.totalCapped - 5000, 0);
    let halfTotalLessThreshold = customRound(totalLessThreshold * 0.5);

    let cappedHalfTotalLessThreshold = Math.min(Math.max(halfTotalLessThreshold, 5000), 150000);   //Grant min = $5,000 and max = $150,000

    //Minimum expenditure = $15,000
    let grantEstimate = expenditure.total < 15000 ? 0 : cappedHalfTotalLessThreshold;

    //Export performance test
    let exportFactor = 0.05;
    let exportTestAmount = 0;
    let performanceMeasureRequired = props.model.grantsReceived
        && props.model.grantsReceivedCount >= 2
        && props.model.businessCategory != BusinessCategories.ApprovedBody;

    if (performanceMeasureRequired && props.model.performanceMeasure == PerformanceMeasures.OptionA) {

        switch (props.model.grantsReceivedCount) {
            case 2:
                exportFactor = 0.4;
                break;
            case 3:
                exportFactor = 0.2;
                break;
            case 4:
                exportFactor = 0.1;
                break;
            case 5:
                exportFactor = 0.075;
                break;
        }

        exportTestAmount = customRound(earnigs.total * exportFactor);

        if (exportTestAmount < 0)   //Grant cannote be -ve, make it 0
            exportTestAmount = 0;

        if (grantEstimate > exportTestAmount)
            grantEstimate = exportTestAmount;
    }

    //Round to whole number
    grantEstimate = customRound(grantEstimate);

    // #region Expense Summary

    var expenseSummaryRows: Option<string>[] = []

    if (props.model.claimOverseasRepExpenses)
        expenseSummaryRows.push({ label: 'Schedule 1A: Overseas representative', value: `$${formatNumber(expenditure.representationTotal)}` });
    if (props.model.claimMarketingConsultantExpenses)
        expenseSummaryRows.push({ label: 'Schedule 1B: Marketing consultant', value: `$${formatNumber(expenditure.marketingConsultantTotal)}` });
    if (props.model.claimMarketingVisitExpenses)
        expenseSummaryRows.push({ label: 'Schedule 2: Marketing visits', value: `$${formatNumber(expenditure.marketingVisitTotal)}` });
    if (props.model.claimFreeSampleExpenses)
        expenseSummaryRows.push({ label: 'Schedule 4: Free samples', value: `$${formatNumber(expenditure.freeSampleTotal)}` });
    if (props.model.claimTradeFairsPromotionalEventsExpenses)
        expenseSummaryRows.push({ label: 'Schedule 5: Trade fairs and promotional events', value: `$${formatNumber(expenditure.promotionalEventTotal)}` });
    if (props.model.claimPromotionalLiteratureAdvertisingExpenses)
        expenseSummaryRows.push({ label: 'Schedule 6: Promotional literature and advertising', value: `$${formatNumber(expenditure.advertisingTotal)}` });
    if (props.model.claimOverseasBuyersExpenses)
        expenseSummaryRows.push({ label: 'Schedule 7: Overseas buyers', value: `$${formatNumber(expenditure.overseasBuyerTotal)}` });
    if (props.model.claimIpRegistrationExpenses)
        expenseSummaryRows.push({ label: 'Schedule 8: Registration of eligible intellectual property (IP)', value: `$${formatNumber(expenditure.intellectualPropertyRegistrationTotal)}` });
    if (props.model.claimIpInsuranceExpenses)
        expenseSummaryRows.push({ label: 'Schedule 9: Insurance of eligible intellectual property (IP)', value: `$${formatNumber(expenditure.intellectualPropertyInsuranceTotal)}` });

    // #endregion Expense Summary

    // #region Earnings Summary

    var earningSummaryRows: Option<string>[] = []

    if (props.model.claimGoodEarnings)
        earningSummaryRows.push({ label: 'Schedule 10A: Goods', value: `$${formatNumber(earnigs.goodsTotal)}` });

    if (props.model.claimServiceEarnings)
        earningSummaryRows.push({ label: 'Schedule 10B: Services/IP Rights/Know-how', value: `$${formatNumber(earnigs.servicesIPRKnowHowTotal)}` });

    if (props.model.claimTourismEarnings)
        earningSummaryRows.push({ label: 'Schedule 10C: Tourism services', value: `$${formatNumber(earnigs.tourismServicesTotal)}` });

    if (props.model.claimEventEarnings)
        earningSummaryRows.push({ label: 'Schedule 10D: Export earnings - Events promoters', value: `$${formatNumber(earnigs.eventPromoterTotal)}` });

    if (props.model.claimEntityEarnings)
        earningSummaryRows.push({ label: 'Schedule 10E: Export earnings - Related entities', value: `$${formatNumber(earnigs.relatedEntityTotal)}` });

    // #endregion Earnings Summary

    // #region Grant estimate

    var grantEstimateRows: Option<string>[] = [
        {
            label: 'Total eligible expenses as entered:',
            value: `$${formatNumber(expenditure.total)}`
        },
        {
            label: 'Eligible expenses after capping:',
            value: `$${formatNumber(expenditure.totalCapped)}`
        },
        {
            label: 'Less expenditure threshold:',
            value: `$${formatNumber(5000)}`
        },
        {
            label: 'Sub-total expenses:',
            value: `$${formatNumber(totalLessThreshold)}`
        },
        {
            label: '50% of sub-total of expenses:',
            value: `$${formatNumber(halfTotalLessThreshold)}`
        },
        {
            label: 'Total:',
            value: `$${formatNumber(cappedHalfTotalLessThreshold)} (min $5,000, max $150,000)`
        },
        {
            label: 'Total export earnings as entered:',
            value: `$${formatNumber(earnigs.total)}`
        }
    ];

    if (performanceMeasureRequired) {
        let choosenPerformanceMeasure = ""

        if (props.model.performanceMeasure == 1) {
            choosenPerformanceMeasure = "Option A";

            grantEstimateRows.push({
                label: 'Export earning percentage:',
                value: `${exportFactor * 100}%`
            });

            grantEstimateRows.push({
                label: 'Total:',
                value: `$${formatNumber(exportTestAmount)}`
            });
        }
        else if (props.model.performanceMeasure == 2) {
            choosenPerformanceMeasure = "Option B";
        }

        grantEstimateRows.unshift({
            label: 'EMDG performance measure:',
            value: choosenPerformanceMeasure
        });
    }

    grantEstimateRows.unshift({
        label: 'Grants number:',
        value: `${props.model.grantsReceivedCount == null ? 1 : props.model.grantsReceivedCount + 1}`
    });

    // #endregion Grant estimate

    return <div>
        <div style={{ display: props.showSummary ? "" : "none" }}>
            <h3>Expenses Summary</h3>
            <table className="table table-sm mt-3">
                <thead>
                    <tr>
                        <th style={{ width: "60%" }}>Schedules summary</th>
                        <th style={{ width: "40%" }}>Total</th>
                    </tr>                                
                </thead>
                <tbody>
                    {expenseSummaryRows.map((r, i) => <tr key={i}>
                        <td>{r.label}</td>
                        <td>{r.value}</td>
                    </tr>
                    )}
                </tbody>
                <tfoot className="table-summary-top table-summary-bottom">
                    <tr>
                        <th>Total:</th>
                        <th>{`$${formatNumber(expenditure.total)}`}</th>
                    </tr>
                </tfoot>
            </table>

            <h3>Export Earnings Summary</h3>
            <table className="table table-sm mt-3">
                <thead>
                    <tr>
                        <th style={{ width: "60%" }}>Schedules summary</th>
                        <th style={{ width: "40%" }}>Total</th>
                    </tr>
                </thead>
                <tbody>
                    {earningSummaryRows.map((r, i) => <tr key={i}>
                        <td>{r.label}</td>
                        <td>{r.value}</td>
                    </tr>
                    )}
                </tbody>
                <tfoot className="table-summary-top table-summary-bottom">
                    <tr>
                        <th>Total:</th>
                        <th>{`$${formatNumber(earnigs.total)}`}</th>
                    </tr>
                </tfoot>
            </table>
        </div>

        <h3>Grant Estimation</h3>
        <p style={{ display: performanceMeasureRequired && props.model.performanceMeasure == PerformanceMeasures.OptionA && halfTotalLessThreshold > exportTestAmount && props.showSummary ? "" : "none" }}>
            Your estimated provisional grant amount has been reduced by the Option A export performance test.
            You can elect to apply the Option B (significant net benefit test) on the previous page to potentially receive a greater grant but this will require significantly more information and may not be successful.
            Please choose carefully as the election cannot be changed after the application has been submitted.
        </p>
        <table className="table table-bordeless table-sm mt-3" style={{ backgroundColor: '#dde1e2' }}>
            <tbody>
                {grantEstimateRows.map((r, i) => <tr key={i}>
                    <td className={r.label == "Total:" ? "float-right" : ""}>{r.label}</td>
                    <td>{r.value}</td>
                </tr>
                )}
            </tbody>
            <tfoot>
                <tr>
                    <th style={{ width: "60%" }}><h4><div className="float-right">Estimated grant entitlement:</div></h4></th>
                    <th style={{ width: "40%" }}><h4>{`$${formatNumber(grantEstimate)}`}</h4></th>
                </tr>
            </tfoot>
        </table>
    </div>
}